import {
	Component,
	Input,
	Output,
	EventEmitter,
	OnChanges,
	SimpleChanges,
	OnDestroy,
} from '@angular/core';
import { NgControl, FormBuilder, Validators, FormGroup } from '@angular/forms';
import {
	FormConfig,
	CustomFormAccessor,
} from 'src/shared/form-models/form-cva.component';
import { BillingCompany } from '../billing-details.models';
import {
	Address,
	Company,
	Railroad,
	Customer,
	CompanyAddress,
} from 'src/shared/api';
import { takeUntil, map } from 'rxjs/operators';
import {
	mapCompanyToBillingCompany,
	mapBillingCompanyToCompany,
	mapRailroadOrCustomerToCompany,
} from '../billing-details.utility';

export const billingCompanyFormConfig: FormConfig<BillingCompany> = {
	id: [''],
	name: ['', Validators.required],
	employerIdentificationNumber: [''],
	contactId: [''],
	contactFirstName: ['', Validators.required],
	contactLastName: ['', Validators.required],
	phoneNumberId: [''],
	phoneNumber: [''],
	address: [''],
};

@Component({
	selector: 'rpshared-billing-company',
	templateUrl: './billing-company.component.html',
})
export class BillingCompanyComponent
	extends CustomFormAccessor<Company | BillingCompany>
	implements OnDestroy {
	availableAddresses: CompanyAddress[] = [];
	allowAddressSelection = false;

	constructor(public ngControl: NgControl, public fb: FormBuilder) {
		super(ngControl, fb, billingCompanyFormConfig);
	}

	writeValue(value: Company): void {
		const billingCompany = mapCompanyToBillingCompany(value);
		this.form.reset(billingCompany, { emitEvent: false });
	}

	onAddressSelected(companyAddress: CompanyAddress) {
		this.form.get('address').patchValue(companyAddress.address);
	}

	registerOnChange(fn: (value: BillingCompany | Company) => void) {
		this.form.valueChanges
			.pipe(map(mapBillingCompanyToCompany), takeUntil(this.destroyed))
			.subscribe(fn);
	}

	ngOnDestroy() {
		this.form.disable();
		super.ngOnDestroy();
	}
}
@Component({
	selector: 'rpshared-billing-company-readonly',
	templateUrl: './billing-company.component.html',
})
export class ReadonlyBillingCompanyComponent implements OnChanges {
	availableAddresses: CompanyAddress[] = [];

	@Input() company: Railroad | Customer;
	@Input() allowAddressSelection = false;
	@Input() currentBillingAddressId: number | null = null;
	@Output() addressSelected = new EventEmitter<Address>();

	readonly form = this.fb.group(billingCompanyFormConfig) as FormGroup;

	constructor(private readonly fb: FormBuilder) {}

	ngOnChanges(changes: SimpleChanges) {
		if (changes.company || changes.currentBillingAddressId) {
			this.updateForm();
		}
	}

	onAddressSelected(companyAddress: CompanyAddress) {
		this.form.get('address').setValue(companyAddress.address, { emitEvent: true });
		this.addressSelected.emit(companyAddress.address);
	}

	private updateForm() {
		if (this.company) {
			const asCompany = mapRailroadOrCustomerToCompany(this.company);
			this.availableAddresses = asCompany.companyAddresses ?? [];

			const preferredAddress = this.currentBillingAddressId
				? this.availableAddresses.find(
						(ca) => ca.address?.id === this.currentBillingAddressId
				  )?.address
				: undefined;

			const billingCompany = mapCompanyToBillingCompany(asCompany);
			if (preferredAddress) {
				billingCompany.address = preferredAddress;
			}

			this.form.patchValue(billingCompany);
			this.form.disable();
		} else {
			this.availableAddresses = [];
			this.form.reset();
			this.form.disable();
		}
	}
}
